import { RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Tooltip } from '@mui/material';
import PlanOverview from './organisms/PlanOverview';
import ProductCardSimple from './organisms/ProductCardSimple';
import useAddToCart from '../../hooks/useAddToCart';
import imgBannerAnnualPlan from '../../assets/img/png/annual-plan.png';
import { PRODUCTS_SLUG } from '../../shared/constants/SlugsProducts';
import { formatPrice } from '../../shared/utils/general.util';
import { setShowModalCheckout } from '../../redux/slices/plans.slice';
import { DATES } from '../../shared/utils/dates';
import { ArrowCounterClockwise, Barricade, Info } from '@phosphor-icons/react';
import Text from '../../components/atoms/Text';
import { getDiscountPeriod } from './molecules/AnnualCountdown';
import AnnualCountdown from './molecules/AnnualCountdown';
import ModalPrebilling from '../../components/organisms/ModalPrebilling';
import URL_PARAMS from '../../shared/constants/urlParams';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
	listSubscriptionsWithPaymentInfo,
	prebillingAvailable,
} from '../../services/suscription.service';
import { SubscriptionStatusEnum } from '../../@types/SubscriptionWithPaymentInfo.enum';
import ModalFailedRenewal from '../../components/organisms/ModalFailedRenewal';
import usePurchases from '../../hooks/usePurchases';

export const ENABLE_PREBILLING_FOR_YEAR = 2024;
const ANNUAL_DECLARATION_PRODUCT_VERSION_ID = 130;

const ANNUAL_BENEFITS = [
	'Usamos nuestros modelos más avanzados de inteligencia artificial buscando optimizar el resultado de tu declaración anual a tu favor.',
	'Recibirás un análisis detallado de la declaración anual 2024 presentada junto con su acuse y detalle correspondiente.',
	'En caso de que el resultado de tu declaración anual sea un saldo a favor, gestionaremos ante el SAT la solicitud correspondiente.',
	'Actualizaremos tu tablero de obligaciones fiscales reflejando el cumplimiento de tu declaración.',
];

enum SubscriptionAnnualStatus {
	PREBILLING = 'prebilling',
	ACQUIRED = 'acquired',
	FAILED_RENEWAL = 'failed_renewal',
}

export const AnnualCard = ({
	subscriptionAnnualStatus,
	isAcquiredProp,
	isLoading,
	onClick,
}: {
	subscriptionAnnualStatus?: SubscriptionAnnualStatus;
	isAcquiredProp?: boolean;
	isLoading?: boolean;
	onClick?: () => void;
}) => {
	const dispatch = useDispatch();
	const { userHasAnnualSubscription } = usePurchases();
	const { handleAddCustomProduct, isLoading: isLoadingAddToCart } =
		useAddToCart();

	const { products } = useSelector((state: RootState) => state.productList);

	const regularizationProducts = products?.find(
		(product) => product.name === 'Declaración Anual',
	)?.products;

	const subscriptionProduct = regularizationProducts?.find(
		(product) => product.slug === PRODUCTS_SLUG.ANNUAL_DECLARATION,
	);

	const price = subscriptionProduct?.product_versions?.[0]?.price;
	const priceWithDiscount = price
		? price * (1 - getDiscountPeriod().discount)
		: 0;
	const isAcquired = userHasAnnualSubscription || !!isAcquiredProp;

	const handleAddToCart = async () => {
		const payload = [
			{
				product_version_id: subscriptionProduct?.default_product_version_id,
				additional_information: {
					regimes: [],
					year: DATES.currentYear - 1,
				},
			},
		];

		const response = await handleAddCustomProduct({
			payload: payload,
			navigateToCart: false,
			resetOrders: true,
		});

		if (!response) return;
		dispatch(setShowModalCheckout(true));
	};

	if (!subscriptionProduct) {
		return (
			<div className='flex items-center justify-center mt-10'>
				<CircularProgress size={25} />
			</div>
		);
	}

	const getLabelCta = () => {
		if (subscriptionAnnualStatus === SubscriptionAnnualStatus.FAILED_RENEWAL) {
			return 'Reintentar pago';
		} else if (
			subscriptionAnnualStatus === SubscriptionAnnualStatus.PREBILLING
		) {
			return 'Adelantar mi renovación';
		}
		return 'Contratar declaración anual 2024';
	};

	const getButtonType = () => {
		if (subscriptionAnnualStatus === SubscriptionAnnualStatus.FAILED_RENEWAL) {
			return 'primary';
		} else if (
			subscriptionAnnualStatus === SubscriptionAnnualStatus.PREBILLING
		) {
			return 'green';
		}
		return 'primary';
	};
	const getCustomContent = () => {
		if (subscriptionAnnualStatus === SubscriptionAnnualStatus.FAILED_RENEWAL) {
			return (
				<div className='max-w-md mx-auto'>
					<div className='rounded-lg border-2 border-[#E09D28] bg-amber-50 p-4 shadow-sm'>
						<div className='flex items-start gap-2'>
							<div className='text-[#E09D28]'>
								<Barricade
									size={24}
									weight='duotone'
								/>
							</div>
							<p className='text-gray-700 text-sm font-medium'>
								Tu declaración anual se encuentra en pausa debido a que no
								pudimos realizar la renovación.
							</p>
						</div>
					</div>
				</div>
			);
		}

		if (
			subscriptionAnnualStatus === SubscriptionAnnualStatus.PREBILLING &&
			!isAcquired
		) {
			return (
				<div className='flex flex-row items-center p-4  max-sm:p-4 rounded-xl gap-2 flex-wrap border border-[#6BA233] bg-[#F7F8F4] w-full'>
					<span className='sm:text-base break-words  lg:text-base inline-block'>
						<b>
							En el 2024 te suscribiste a nuestro plan de declaración anual.{' '}
						</b>
						Sin embargo, tu renovación aún no se ha generado. Por favor haz clic
						en adelantar mi renovación para empezar a preparar tu declaración
						anual.
					</span>
				</div>
			);
		}
		return null;
	};
	const getCustomButtonIcon = () => {
		if (
			[
				SubscriptionAnnualStatus.FAILED_RENEWAL,
				SubscriptionAnnualStatus.PREBILLING,
			].includes(subscriptionAnnualStatus!)
		) {
			return (
				<ArrowCounterClockwise
					weight='bold'
					className='size-6'
				/>
			);
		}
		return null;
	};
	return (
		<div className=''>
			<AnnualCountdown page='annual_plans' />
			{/* ======== PRODUCT CARD ======== */}
			<div className='flex flex-col gap-8'>
				<ProductCardSimple
					isLoading={isLoadingAddToCart?.products || isLoading}
					imgBanner={imgBannerAnnualPlan}
					name={
						<div>
							Declaración <br />{' '}
							<span className='flex items-center gap-1'>
								anual 2024{' '}
								<Tooltip
									title='Actualizamos el nombre de la declaración anual a presentar este año de 2025 a 2024 para que sea más claro para ti y estemos en línea con la comunicación del SAT.'
									placement='top'
								>
									<Info />
								</Tooltip>
							</span>
						</div>
					}
					priceDescription={getDiscountPeriod().description}
					description='Cubre la presentación de tu declaración anual 2024. Anticipa tu pago y asegura una preparación a tiempo.'
					benefitsTitle='Tu declaración anual incluye'
					benefits={ANNUAL_BENEFITS}
					ctaAction={
						subscriptionAnnualStatus && onClick ? onClick : handleAddToCart
					}
					ctaLabel={getLabelCta()}
					price={formatPrice(priceWithDiscount)}
					className='max-w-[400px] mx-auto'
					type={getButtonType()}
					customContent={getCustomContent()}
					isAdquired={isAcquired}
					customButtonIcon={getCustomButtonIcon()}
				/>
				<div className='!text-h-neutral-40 p-2 rounded-lg bg-h-neutral-90/50 flex items-start gap-2 sm:-mt-4 max-w-[400px] mx-auto'>
					<Info className='min-w-5 min-h-5' />
					<div className='flex flex-col gap-1'>
						<Text size='caption'>Importante</Text>
						<Text
							size='mini-1'
							color='medium'
							weight='light'
						>
							Recuerda que para presentar tu declaración anual, todas tus
							declaraciones mensuales deben estar correctamente presentadas. En
							caso de que no lo estén, uno de nuestros contadores se comunicará
							contigo para ayudarte a regularizar o corregir las mismas y así
							poder presentar tu declaración anual.
						</Text>
					</div>
				</div>
			</div>
		</div>
	);
};

const AnnualPlanSection = () => {
	const [showModalPrebilling, setShowModalPrebilling] = useState(false);
	const [showModalFailedRenewal, setShowModalFailedRenewal] = useState(false);
	const [subscriptionAnnualId, setSubscriptionAnnualId] = useState<
		number | null
	>(null);
	const { tokenSession } = useSelector((state: RootState) => state.auth);
	const [searchParams, setSearchParams] = useSearchParams();
	const prebillingParam = searchParams.get(URL_PARAMS.PREBILLING);
	const [isLoading, setIsLoading] = useState(false);
	const [isAcquired, setIsAcquired] = useState(false);
	const [subscriptionAnnualStatus, setSubscriptionAnnualStatus] =
		useState<SubscriptionAnnualStatus | null>(null);

	const getStatusSubscription = async () => {
		try {
			if (isLoading) return;
			setIsLoading(true);
			const [prebillingResponse, subscriptionsResponse] = await Promise.all([
				prebillingAvailable(tokenSession),
				listSubscriptionsWithPaymentInfo(tokenSession),
			]);

			const searchAnnualFailedRenewal = subscriptionsResponse.find(
				(subscription) =>
					subscription.product_version_id ===
						ANNUAL_DECLARATION_PRODUCT_VERSION_ID &&
					subscription.status === SubscriptionStatusEnum.FAILED,
			);
			if (searchAnnualFailedRenewal) {
				setSubscriptionAnnualStatus(SubscriptionAnnualStatus.FAILED_RENEWAL);
				setSubscriptionAnnualId(searchAnnualFailedRenewal.id);
				return;
			}

			// Validate Status Prebilling
			const subscriptionsAnnual = prebillingResponse.annual;
			if (subscriptionsAnnual.length > 0) {
				const [subscriptionAnnual] = subscriptionsAnnual;
				if (
					!subscriptionAnnual.years_covered.includes(ENABLE_PREBILLING_FOR_YEAR)
				) {
					setSubscriptionAnnualStatus(SubscriptionAnnualStatus.PREBILLING);
					setSubscriptionAnnualId(subscriptionAnnual.id);
				}
			}
		} catch (error) {
		} finally {
			setIsLoading(false);
		}
	};

	const onClosePrebillingModal = (isSuccess: boolean) => {
		setShowModalPrebilling(false);
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete(URL_PARAMS.PREBILLING);
		newSearchParams.delete(URL_PARAMS.FLOW);
		setSearchParams(newSearchParams);
		if (isSuccess) {
			setIsAcquired(true);
			setSubscriptionAnnualId(null);
		}
	};

	useEffect(() => {
		getStatusSubscription();
	}, []);

	useEffect(() => {
		if (prebillingParam === 'annual' && subscriptionAnnualId) {
			setShowModalPrebilling(true);
		}
	}, [prebillingParam, subscriptionAnnualId]);

	return (
		<div className='max-w-[860px] mx-auto relative'>
			{showModalPrebilling && (
				<ModalPrebilling
					showModal={showModalPrebilling}
					setShowModal={setShowModalPrebilling}
					subscriptionId={subscriptionAnnualId}
					onClose={onClosePrebillingModal}
				/>
			)}

			{showModalFailedRenewal && (
				<ModalFailedRenewal
					showModal={showModalFailedRenewal}
					setShowModal={setShowModalFailedRenewal}
					subscriptionId={subscriptionAnnualId!}
				/>
			)}
			<PlanOverview
				title='Declaración anual 2024'
				description={
					<>
						Asegura hoy tu declaración anual a presentar en{' '}
						<b className='text-h-neutral-0 font-bold'>
							abril {DATES.currentYear}.
						</b>{' '}
						Nuestros expertos fiscales junto con nuestra tecnología te brindarán
						un proceso claro, sencillo y sin preocupaciones para cumplir
						correctamente con el SAT.
					</>
				}
			/>

			{/* ======== COUNTDOWN ======== */}
			<div className='px-2'>
				<AnnualCard
					subscriptionAnnualStatus={subscriptionAnnualStatus!}
					isLoading={isLoading}
					isAcquiredProp={isAcquired}
					onClick={() => {
						if (
							SubscriptionAnnualStatus.PREBILLING === subscriptionAnnualStatus
						) {
							setShowModalPrebilling(true);
						}
						if (
							SubscriptionAnnualStatus.FAILED_RENEWAL ===
							subscriptionAnnualStatus
						) {
							setShowModalFailedRenewal(true);
						}
					}}
				/>
			</div>
		</div>
	);
};

export default AnnualPlanSection;
